import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Upload } from "lucide-react";
import { PublicHeader } from "../components/PublicHeader";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const CALENDLY = "https://calendly.com/d/dtgj-6kj-rpp/careers-call-candidate";
const WILLING = ["Sydney", "Melbourne", "Brisbane", "Perth", "Adelaide", "Gold Coast", "Regional", "Remote"];
const EMP_TYPES = ["Full-time", "Part-time", "Contract", "Casual"];
const YEARS = ["0–2", "3–5", "6–10", "10+"];
const NOTICE = ["Immediately", "1 week", "2 weeks", "4 weeks", "2 months", "2+ months"];
const RIGHTS = ["Australian Citizen", "Permanent Resident", "Visa holder", "No current working rights"];

const inputCls = "mt-1.5 w-full rounded-lg border border-[#e3e0d3] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#94a01a]";
const labelCls = "text-xs uppercase tracking-[0.15em] font-medium text-[#51637a]";

const Section = ({ n, title, children }) => (
  <section className="rounded-2xl border border-[#e3e0d3] bg-white p-7 sm:p-8" data-testid={`section-${n}`}>
    <div className="mb-6 flex items-center gap-3">
      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#0d2f4d] text-sm font-bold text-[#C1CC24]">{n}</span>
      <h2 className="font-heading text-xl font-bold text-[#0d2f4d]">{title}</h2>
    </div>
    <div className="space-y-5">{children}</div>
  </section>
);
const Chips = ({ options, value, onToggle, testid }) => (
  <div className="flex flex-wrap gap-2" data-testid={testid}>
    {options.map((o) => {
      const on = value.includes(o);
      return (
        <button type="button" key={o} onClick={() => onToggle(o)}
          className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${on ? "border-[#0d2f4d] bg-[#0d2f4d] text-[#C1CC24]" : "border-[#e3e0d3] bg-white text-[#0d2f4d] hover:border-[#0d2f4d]"}`}>
          {o}
        </button>
      );
    })}
  </div>
);
const YesNo = ({ value, onChange, testid }) => (
  <div className="mt-1.5 flex gap-2" data-testid={testid}>
    {[["Yes", true], ["No", false]].map(([lbl, val]) => (
      <button type="button" key={lbl} onClick={() => onChange(val)} data-testid={`${testid}-${lbl.toLowerCase()}`}
        className={`rounded-full border px-6 py-2 text-sm font-medium ${value === val ? "border-[#0d2f4d] bg-[#0d2f4d] text-[#C1CC24]" : "border-[#e3e0d3] bg-white text-[#0d2f4d] hover:border-[#0d2f4d]"}`}>{lbl}</button>
    ))}
  </div>
);

export default function EliteIntake() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [busy, setBusy] = useState(false);
  const [booked, setBooked] = useState(false);
  const [f, setF] = useState({
    first_name: "", last_name: "", email: "", password: "", mobile: "", website: "",
    current_location: "", target_location: "", relocating: null,
    work_rights_status: "", visa_type: "", visa_expiry: "",
    current_job_title: "", industry: "", years_experience: "", roles: "",
    willing_to_work: [], employment_type: [], salary_range: "", notice_period: "", start_date: "", actively_seeking: null,
    cv_base64: "", cv_filename: "", linkedin_url: "", career_goals: "", non_negotiables: "", notes_before_call: "",
    social_media_consent: null,
    consent_storage: false, consent_no_guarantee: false, consent_approval: false,
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const toggle = (k, o) => setF((p) => ({ ...p, [k]: p[k].includes(o) ? p[k].filter((x) => x !== o) : [...p[k], o] }));
  const onCv = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) return toast.error("CV must be under 10MB");
    const r = new FileReader();
    r.onload = () => setF((p) => ({ ...p, cv_base64: r.result, cv_filename: file.name }));
    r.readAsDataURL(file);
  };

  const submit = async () => {
    if (!f.first_name || !f.last_name || !f.email || f.password.length < 8 || !f.mobile)
      return toast.error("Please complete your name, email, phone and an 8+ char password");
    if (!f.work_rights_status)
      return toast.error("Please tell us your Australian work rights / visa status");
    if (!(f.consent_storage && f.consent_no_guarantee && f.consent_approval))
      return toast.error("Please tick all three consent boxes");
    setBusy(true);
    try {
      const res = await api.post("/auth/register/elite", f);
      setUser(res.data);
      toast.success("Profile created — book your screening call below");
      setBooked(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (booked) {
    return (
      <div className="min-h-screen bg-[#f5f3ee]" data-testid="booking-stage">
        <PublicHeader />
        <main className="mx-auto max-w-3xl px-6 pb-20 pt-28 text-center">
          <div className="mb-5 inline-flex items-center gap-2 rounded-full bg-[#e6ecc4] px-4 py-1.5 text-sm font-semibold text-[#5c6410]">
            <CheckCircle2 className="h-4 w-4" /> Profile created
          </div>
          <h1 className="font-heading text-3xl font-bold text-[#0d2f4d] sm:text-4xl">Book your screening call</h1>
          <p className="mx-auto mt-3 max-w-xl text-[#51637a]">Grab a time with our recruitment team below. Once booked, head to your dashboard to keep your profile up to date.</p>
          <iframe title="Book your call" src={CALENDLY} data-testid="calendly-widget"
            className="mt-8 h-[720px] w-full rounded-2xl border border-[#e3e0d3] bg-white" />
          <button onClick={() => navigate("/candidate")} data-testid="go-to-dashboard"
            className="mt-6 inline-flex rounded-full bg-[#0d2f4d] px-7 py-3.5 text-sm font-semibold text-white hover:bg-[#1c4d78]">
            I've booked — go to my dashboard
          </button>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f5f3ee]" data-testid="elite-intake-page">
      <PublicHeader />
      <main className="mx-auto max-w-3xl px-6 pb-24 pt-28">
        <header className="mb-10 text-center">
          <p className="font-accent text-xl text-[#7C8514]">Candidate Discovery Form</p>
          <h1 className="mt-2 font-heading text-4xl font-bold tracking-tight text-[#0d2f4d] sm:text-5xl">Create your free candidate profile</h1>
          <p className="mx-auto mt-3 max-w-xl text-[#51637a]">Tell us about your experience, career goals and what you're looking for. If we have a genuine vacancy that matches your background, our recruitment team may contact you.</p>
          <p className="mx-auto mt-3 max-w-xl text-xs text-[#7C8514] font-medium" data-testid="free-note">Free candidate registration. There is no fee to be considered for roles through The Ditch Careers.</p>
        </header>

        <div className="space-y-6">
          <Section n={1} title="About you">
            <div className="grid gap-4 sm:grid-cols-2">
              <div><label className={labelCls}>First name</label><input className={inputCls} data-testid="f-first" value={f.first_name} onChange={(e) => set("first_name", e.target.value)} /></div>
              <div><label className={labelCls}>Last name</label><input className={inputCls} data-testid="f-last" value={f.last_name} onChange={(e) => set("last_name", e.target.value)} /></div>
              <div><label className={labelCls}>Email</label><input type="email" className={inputCls} data-testid="f-email" value={f.email} onChange={(e) => set("email", e.target.value)} /></div>
              <div><label className={labelCls}>Phone</label><input className={inputCls} data-testid="f-mobile" value={f.mobile} onChange={(e) => set("mobile", e.target.value)} /></div>
              <div className="sm:col-span-2"><label className={labelCls}>Password (8+ characters)</label><input type="password" className={inputCls} data-testid="f-password" value={f.password} onChange={(e) => set("password", e.target.value)} /></div>
            </div>
            <div><label className={labelCls}>Current location</label><input className={inputCls} data-testid="f-location" value={f.current_location} onChange={(e) => set("current_location", e.target.value)} placeholder="e.g. Bondi, NSW — or your city/country if overseas" /></div>
            <div><label className={labelCls}>Are you relocating to Australia?</label><YesNo testid="f-relocating" value={f.relocating} onChange={(v) => set("relocating", v)} /></div>
          </Section>

          <Section n={2} title="Work rights & target location">
            <div>
              <label className={labelCls}>Australian work rights / visa status</label>
              <select className={inputCls} data-testid="f-rights" value={f.work_rights_status} onChange={(e) => set("work_rights_status", e.target.value)}>
                <option value="">Select…</option>{RIGHTS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            {f.work_rights_status === "Visa holder" && (
              <div className="grid gap-4 sm:grid-cols-2" data-testid="f-visa-conditional">
                <div><label className={labelCls}>Visa type</label><input className={inputCls} data-testid="f-visa-type" value={f.visa_type} onChange={(e) => set("visa_type", e.target.value)} /></div>
                <div><label className={labelCls}>Visa expiry</label><input type="date" className={inputCls} data-testid="f-visa-expiry" value={f.visa_expiry} onChange={(e) => set("visa_expiry", e.target.value)} /></div>
              </div>
            )}
            <div><label className={labelCls}>Australian city or state you're targeting</label><input className={inputCls} data-testid="f-target" value={f.target_location} onChange={(e) => set("target_location", e.target.value)} placeholder="e.g. Sydney, NSW" /></div>
          </Section>

          <Section n={3} title="Your experience">
            <div className="grid gap-4 sm:grid-cols-2">
              <div><label className={labelCls}>Current job title</label><input className={inputCls} data-testid="f-jobtitle" value={f.current_job_title} onChange={(e) => set("current_job_title", e.target.value)} /></div>
              <div><label className={labelCls}>Industry</label><input className={inputCls} data-testid="f-industry" value={f.industry} onChange={(e) => set("industry", e.target.value)} placeholder="e.g. Hospitality, Construction" /></div>
            </div>
            <div><label className={labelCls}>Years of experience</label><div className="mt-1.5"><Chips testid="f-years" options={YEARS} value={f.years_experience ? [f.years_experience] : []} onToggle={(o) => set("years_experience", f.years_experience === o ? "" : o)} /></div></div>
            <div><label className={labelCls}>Roles you're interested in</label><input className={inputCls} data-testid="f-roles" value={f.roles} onChange={(e) => set("roles", e.target.value)} placeholder="e.g. Head Chef, Sous Chef" /></div>
          </Section>

          <Section n={4} title="What you're looking for">
            <div><label className={labelCls}>Preferred location</label><div className="mt-1.5"><Chips testid="f-willing" options={WILLING} value={f.willing_to_work} onToggle={(o) => toggle("willing_to_work", o)} /></div></div>
            <div><label className={labelCls}>Employment type</label><div className="mt-1.5"><Chips testid="f-emptype" options={EMP_TYPES} value={f.employment_type} onToggle={(o) => toggle("employment_type", o)} /></div></div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div><label className={labelCls}>Salary expectations</label><input className={inputCls} data-testid="f-salary" value={f.salary_range} onChange={(e) => set("salary_range", e.target.value)} placeholder="e.g. $85k–$95k + super" /></div>
              <div><label className={labelCls}>Notice period</label><select className={inputCls} data-testid="f-notice" value={f.notice_period} onChange={(e) => set("notice_period", e.target.value)}><option value="">Select…</option>{NOTICE.map((n) => <option key={n} value={n}>{n}</option>)}</select></div>
              <div><label className={labelCls}>Earliest available start date</label><input type="date" className={inputCls} data-testid="f-startdate" value={f.start_date} onChange={(e) => set("start_date", e.target.value)} /></div>
            </div>
            <div><label className={labelCls}>Are you actively seeking employment?</label><YesNo testid="f-seeking" value={f.actively_seeking} onChange={(v) => set("actively_seeking", v)} /></div>
          </Section>

          <Section n={5} title="Your CV & goals">
            <div>
              <label className={labelCls}>CV upload (max 10MB)</label>
              <label className="mt-1.5 flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-[#cfcbbc] bg-white px-4 py-3 text-sm text-[#51637a] hover:border-[#0d2f4d]">
                <Upload className="h-4 w-4" /> {f.cv_filename || "Choose file (PDF/DOC)"}
                <input type="file" accept=".pdf,.doc,.docx" className="hidden" data-testid="f-cv" onChange={onCv} />
              </label>
            </div>
            <div><label className={labelCls}>LinkedIn profile</label><input className={inputCls} data-testid="f-linkedin" value={f.linkedin_url} onChange={(e) => set("linkedin_url", e.target.value)} placeholder="https://linkedin.com/in/…" /></div>
            <div><label className={labelCls}>Career goals</label><textarea rows={4} className={inputCls} data-testid="f-goals" value={f.career_goals} onChange={(e) => set("career_goals", e.target.value)} placeholder="Where you'd like your career to go…" /></div>
            <div><label className={labelCls}>What matters most to you in your next role?</label><textarea rows={3} className={inputCls} data-testid="f-matters" value={f.non_negotiables} onChange={(e) => set("non_negotiables", e.target.value)} placeholder="e.g. culture, flexibility, progression…" /></div>
            <div><label className={labelCls}>Any additional information</label><textarea rows={3} className={inputCls} data-testid="f-notes" value={f.notes_before_call} onChange={(e) => set("notes_before_call", e.target.value)} /></div>
            {/* honeypot */}
            <input type="text" tabIndex={-1} autoComplete="off" value={f.website} onChange={(e) => set("website", e.target.value)} className="hidden" aria-hidden="true" data-testid="f-hp" />
          </Section>

          <Section n={6} title="Profile visibility (optional)">
            <div>
              <label className="text-sm font-medium text-[#0d2f4d]">Would you be happy for The Ditch Careers to advertise an anonymised version of your candidate profile on our social media and employer channels?</label>
              <YesNo testid="f-social" value={f.social_media_consent} onChange={(v) => set("social_media_consent", v)} />
              <p className="mt-3 text-xs text-[#51637a] leading-relaxed">This is optional and will not affect your eligibility for roles. Your surname, contact details and identifying information will not be included without your further permission.</p>
            </div>
          </Section>

          <div className="space-y-3 rounded-2xl border border-[#e3e0d3] bg-white p-7">
            {[["consent_storage", "I consent to The Ditch Careers storing and processing my data."],
              ["consent_no_guarantee", "I understand placement is not guaranteed."],
              ["consent_approval", "I understand my profile will be approved by me before being presented to any employer."]].map(([k, label]) => (
              <label key={k} className="flex items-start gap-3 text-sm text-[#33475b]">
                <input type="checkbox" data-testid={`consent-${k}`} checked={f[k]} onChange={(e) => set(k, e.target.checked)} className="mt-1 h-4 w-4 accent-[#94a01a]" />
                <span>{label}</span>
              </label>
            ))}
          </div>

          <button onClick={submit} disabled={busy} data-testid="submit-elite"
            className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#C1CC24] px-8 py-4 text-base font-bold text-[#0d2f4d] transition-colors hover:bg-[#aeb81f] disabled:opacity-60">
            {busy && <Loader2 className="h-5 w-5 animate-spin" />} Create Your Free Candidate Profile
          </button>
        </div>
      </main>
    </div>
  );
}
